import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask, faXRay, faUser, faFileMedical, faCalendarAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import Resultmodal from './Resultmodal';
import RadiologyResultsModal2 from './RadiologyResultsModal2';
import './PatientsFiles.css';
import Topbar from './Topbar';
import Walkin from './Walkin';

function PatientsFiles() {
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isLabResultsModalOpen, setIsLabResultsModalOpen] = useState(false);
    const [isRadiologyResultsModalOpen, setIsRadiologyResultsModalOpen] = useState(false);
    const [employeeName, setEmployeeName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const [clinicDetails, setClinicDetails] = useState(null);
    const navigate = useNavigate();
    let abortController = new AbortController();
    const [isWalkinModalOpen, setIsWalkinModalOpen] = useState(false);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        const fetchTokenAndCheckSecurity = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const tokenFromUrl = params.get('token');

                const securityResponse = await fetch(urls.security, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token: tokenFromUrl }),
                });

                if (securityResponse.ok) {
                    const securityData = await securityResponse.json();

                    if (securityData.message === 'Session valid') {
                        setEmployeeName(securityData.employee_name);
                        localStorage.setItem('token', securityData.clinic_session_token);
                        fetchPatients(tokenFromUrl);
                        await fetchClinicDetails(tokenFromUrl);
                    } else if (securityData.error === 'Session expired') {
                        navigate(`/dashboard?token=${securityData.clinic_session_token}`);
                    } else {
                        navigate('/login');
                    }
                } else {
                    throw new Error('Failed to perform security check');
                }
            } catch (error) {
                console.error('Error performing security check:', error);
                navigate('/login');
            }
        };

        fetchTokenAndCheckSecurity();
    }, [navigate]);

    useEffect(() => {
        return () => {
            // Clean up the timeout when component unmounts
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            abortController.abort();
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        // Clear previous results while loading new ones
        setPatients([]);

        // Clear any pending timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set a new timeout to debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            fetchPatients();
        }, 1000); // 500ms delay

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, selectedDate]);

    const fetchPatients = useCallback(() => {
        abortController.abort();
        abortController = new AbortController();

        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');

        let apiUrl = `${urls.suggestfiles}?query=${encodeURIComponent(searchQuery)}`;
        if (selectedDate) {
            apiUrl += `&date=${selectedDate}`;
        }

        fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: tokenFromUrl }),
            signal: abortController.signal
        })
            .then(response => response.json())
            .then(data => {
                setPatients(data);
                setLoading(false);
            })
            .catch(error => {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching patient data:', error);
                    setLoading(false);
                }
            });
    }, [searchQuery, selectedDate]);

    const fetchClinicDetails = async (token) => {
        try {
            const response = await fetch(urls.fetchclinicdetails, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });
            if (response.ok) {
                const data = await response.json();
                setClinicDetails(data);
            } else {
                throw new Error('Failed to fetch clinic details');
            }
        } catch (error) {
            console.error('Error fetching clinic details:', error);
        }
    };

    const handleViewEditPatientFile = (fileId) => {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');
        navigate(`/patient-file/${tokenFromUrl}/${fileId}`);
    };

    const handleShowLabResults = (patient) => {
        setSelectedPatient(patient);
        setIsLabResultsModalOpen(true);
    };

    const handleShowRadiologyResults = (patient) => {
        setSelectedPatient(patient);
        setIsRadiologyResultsModalOpen(true);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };

    const handleSeePatientsForScanOrLab = () => {
        setIsWalkinModalOpen(true);
    };

    const formatDateTime = (dateTimeString) => {
        const options = { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true
        };
        return new Date(dateTimeString).toLocaleString('en-US', options);
    };

    return (
        <div style={{
            backgroundColor: '#f5f7fa',
            minHeight: '100vh',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            paddingBottom: '20px'
        }}>
            <Topbar token={urlToken} />
            
            <div style={{
                padding: '15px',
                maxWidth: '100%',
                margin: '0 auto',
                boxSizing: 'border-box',
                paddingTop: '75px' // Added padding to create space below top bar
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: window.innerWidth < 768 ? 'flex-start' : 'center',
                        gap: '10px'
                    }}>
                        <h1 style={{
                            fontSize: window.innerWidth < 768 ? '20px' : '24px',
                            color: '#2c3e50',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <FontAwesomeIcon icon={faFileMedical} style={{ color: '#3498db' }} />
                            Patient Files Management
                        </h1>
                        <p style={{
                            fontSize: window.innerWidth < 768 ? '14px' : '16px',
                            color: '#7f8c8d',
                            margin: 0,
                            fontWeight: '500',
                            textAlign: window.innerWidth < 768 ? 'left' : 'right'
                        }}>{clinicDetails?.clinic_name || 'Clinic'}</p>
                    </div>
                </div>

               

                <div style={{
    display: 'flex',
    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
    width: '100%'
}}>
    {/* Search Input */}
    <div style={{
        flex: '1',
        minWidth: window.innerWidth < 768 ? '100%' : '300px',
        position: 'relative',
        height: '40px' // ensure same height
    }}>
        <FontAwesomeIcon 
            icon={faSearch} 
            style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#95a5a6'
            }} 
        />
        <input
            type="text"
            placeholder="Search by patient name..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
                width: '100%',
                height: '100%',
                padding: '10px 10px 10px 40px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                fontSize: '14px',
                boxSizing: 'border-box'
            }}
        />
    </div>
    
    {/* Date Picker */}
    <div style={{
        position: 'relative',
        minWidth: window.innerWidth < 768 ? '100%' : 'auto',
        height: '40px' // match height
    }}>
        <FontAwesomeIcon 
            icon={faCalendarAlt} 
            style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#95a5a6'
            }} 
        />
        <input
            id="date-picker"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            style={{
                width: window.innerWidth < 768 ? '100%' : 'auto',
                height: '100%',
                padding: '10px 10px 10px 40px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                fontSize: '14px',
                boxSizing: 'border-box'
            }}
        />
    </div>
    
    {/* Button */}
    <button 
        onClick={handleSeePatientsForScanOrLab}
        style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            height: '40px', // match height
            padding: '0 15px',
            borderRadius: '5px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.3s',
            whiteSpace: 'nowrap',
            width: window.innerWidth < 768 ? '100%' : 'auto'
        }}
    >
        <FontAwesomeIcon icon={faUser} />
        Scan/Lab Only Patients
    </button>
</div>

                {loading ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '200px',
                        width: '100%'
                    }}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{
                            marginTop: '15px',
                            color: '#7f8c8d'
                        }}>Loading patient files...</p>
                    </div>
                ) : (
                    <div style={{
                        width: '100%',
                        overflowX: 'auto'
                    }}>
                        {patients.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: window.innerWidth < 600 ? '1fr' : 
                                                  window.innerWidth < 900 ? 'repeat(2, 1fr)' : 
                                                  window.innerWidth < 1200 ? 'repeat(3, 1fr)' : 
                                                  'repeat(4, 1fr)',
                                gap: '15px',
                                width: '100%'
                            }}>
                                {patients.map(patient => (
                                    <div key={patient.contact_id} style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                                        padding: '15px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: '280px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}>
                                        <div style={{
                                            borderBottom: '1px solid #eee',
                                            paddingBottom: '10px',
                                            marginBottom: '10px'
                                        }}>
                                            <h3 style={{
                                                margin: '0 0 5px 0',
                                                color: '#2c3e50',
                                                fontSize: '16px',
                                                display: 'flex',
                                                flexDirection: window.innerWidth < 400 ? 'column' : 'row',
                                                justifyContent: 'space-between',
                                                textTransform: 'uppercase', // Add this line
                                                gap: '5px'
                                            }}>
                                                <span>{`${patient.first_name} ${patient.last_name}`}</span>
                                                <span style={{
                                                    backgroundColor: '#e8f4fc',
                                                    color: '#3498db',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    alignSelf: 'flex-start'
                                                }}>OPD: {patient.opd_no}</span>
                                                 <span style={{
                                                    backgroundColor: '#e8f4fc',
                                                    color: '#3498db',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    alignSelf: 'flex-start'
                                                }}>File ID: {patient.file_id}</span>
                                                 
                                            </h3>
                                            <div style={{
                                                display: 'flex',
                                                gap: '10px',
                                                fontSize: '13px',
                                                color: '#7f8c8d'
                                            }}>
                                                <span>{patient.age} years</span>
                                                <span>{patient.sex}</span>
                                            </div>
                                        </div>
                                        
                                        <div style={{
                                            flex: '1',
                                            marginBottom: '10px',
                                            overflow: 'hidden'
                                        }}>
                                            <p style={{
                                                margin: '0 0 8px 0',
                                                fontSize: '13px',
                                                color: '#34495e',
                                                wordBreak: 'break-word'
                                            }}>
                                                <strong style={{ color: '#2c3e50', fontSize: '13px' }}>Visit:</strong> {formatDateTime(patient.date_time)}
                                            </p>
                                            
                                            {patient.diagnosis && (
                                                <p style={{
                                                    margin: '0 0 8px 0',
                                                    fontSize: '13px',
                                                    color: '#34495e',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    <strong style={{ color: '#2c3e50', fontSize: '13px' }}>Diagnosis:</strong> {patient.diagnosis}
                                                </p>
                                            )}
                                            
                                            {patient.clinical_notes && (
                                                <p style={{
                                                    margin: '0 0 8px 0',
                                                    fontSize: '13px',
                                                    color: '#34495e',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    <strong style={{ color: '#2c3e50', fontSize: '13px' }}>Notes:</strong> {patient.clinical_notes}
                                                </p>
                                            )}
                                        </div>

                                        <div style={{
                                            marginBottom: '10px'
                                        }}>
                                            {patient.lab_status_message && (
                                                <div 
                                                    onClick={() => patient.lab_status_message.includes('Click here') && handleShowLabResults(patient)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '6px',
                                                        borderRadius: '5px',
                                                        backgroundColor: patient.lab_status_message.includes('Click here') ? '#e8f4fc' : '#f5f5f5',
                                                        marginBottom: '6px',
                                                        fontSize: '12px',
                                                        color: patient.lab_status_message.includes('Click here') ? '#3498db' : '#7f8c8d',
                                                        cursor: patient.lab_status_message.includes('Click here') ? 'pointer' : 'default',
                                                        transition: 'background-color 0.2s',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faFlask} style={{ fontSize: '12px', flexShrink: 0 }} />
                                                    <span>{patient.lab_status_message}</span>
                                                </div>
                                            )}

                                            {patient.radiology_status_message && (
                                                <div 
                                                    onClick={() => patient.radiology_status_message.includes('Click here') && handleShowRadiologyResults(patient)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '6px',
                                                        borderRadius: '5px',
                                                        backgroundColor: patient.radiology_status_message.includes('Click here') ? '#e8f4fc' : '#f5f5f5',
                                                        fontSize: '12px',
                                                        color: patient.radiology_status_message.includes('Click here') ? '#3498db' : '#7f8c8d',
                                                        cursor: patient.radiology_status_message.includes('Click here') ? 'pointer' : 'default',
                                                        transition: 'background-color 0.2s',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faXRay} style={{ fontSize: '12px', flexShrink: 0 }} />
                                                    <span>{patient.radiology_status_message}</span>
                                                </div>
                                            )}
                                        </div>

                                        {patient.amount && patient.amount !== '0.00' && (
                                            <div style={{
                                                marginBottom: '10px',
                                                fontSize: '13px',
                                                color: '#27ae60',
                                                fontWeight: '500'
                                            }}>
                                                <strong style={{ color: '#2c3e50', fontSize: '13px' }}>Amount Spent On Pt:</strong> UGX {parseFloat(patient.amount).toLocaleString()}
                                            </div>
                                        )}

                                        <button 
                                            onClick={() => handleViewEditPatientFile(patient.file_id)}
                                            style={{
                                                backgroundColor: '#3498db',
                                                color: 'white',
                                                border: 'none',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                width: '100%',
                                                transition: 'background-color 0.3s',
                                                ':hover': {
                                                    backgroundColor: '#2980b9'
                                                }
                                            }}
                                        >
                                            View/Edit File
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: '#fff',
                                padding: '20px',
                                borderRadius: '8px',
                                textAlign: 'center',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}>
                                <p style={{
                                    color: '#7f8c8d',
                                    fontSize: '16px'
                                }}>No patient files found matching your criteria.</p>
                            </div>
                        )}
                    </div>
                )}

                {isLabResultsModalOpen && selectedPatient && (
                    <Resultmodal 
                        patient={selectedPatient} 
                        onClose={() => setIsLabResultsModalOpen(false)} 
                        clinicDetails={clinicDetails}
                    />
                )}

                {isRadiologyResultsModalOpen && selectedPatient && (
                    <RadiologyResultsModal2 
                        patient={selectedPatient} 
                        onClose={() => setIsRadiologyResultsModalOpen(false)} 
                        clinicDetails={clinicDetails}
                    />
                )}

                {isWalkinModalOpen && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '20px',
                            maxWidth: '95%',
                            maxHeight: '95%',
                            overflow: 'auto',
                            width: window.innerWidth < 768 ? '95%' : '800px',
                            boxSizing: 'border-box'
                        }}>
                            <Walkin token={urlToken} onClose={() => setIsWalkinModalOpen(false)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PatientsFiles;